import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, Download } from "lucide-react";
import { API } from "@/context/LanguageContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const token = () => localStorage.getItem("rm_admin_token");
const authH = () => ({ Authorization: `Bearer ${token()}` });

const inputCls = "w-full border border-white/15 bg-anthracite-dark px-3 py-2.5 text-sm text-white outline-none focus:border-royal";
const labelCls = "mb-1.5 block font-mono-tech text-[11px] uppercase tracking-widest text-slate-400";

function specsToText(specs) {
  return (specs || []).map((s) => `${s.label_de} | ${s.label_it} | ${s.value}`).join("\n");
}
function textToSpecs(text) {
  return text.split("\n").map((l) => l.split("|").map((x) => x.trim())).filter((p) => p[0])
    .map((p) => ({ label_de: p[0], label_it: p[1] || p[0], value: p[2] || "" }));
}

function MachineForm({ initial, onDone }) {
  const [d, setD] = useState(initial || { name: "", type_de: "", type_it: "", description_de: "", description_it: "", specs: [], image_url: "", order: 0 });
  const [image, setImage] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries({ ...d, specs: JSON.stringify(d.specs) }).forEach(([k, v]) => fd.append(k, v));
      if (image) fd.append("image", image);
      if (d.id) await axios.put(`${API}/admin/machines/${d.id}`, fd, { headers: authH() });
      else await axios.post(`${API}/admin/machines`, fd, { headers: authH() });
      toast.success("Gespeichert");
      onDone();
    } catch {
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-4 border border-white/10 bg-anthracite p-6" data-testid="machine-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2"><label className={labelCls}>Bezeichnung *</label>
          <input required value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} className={inputCls} data-testid="machine-name" /></div>
        <div><label className={labelCls}>Typ (DE)</label>
          <input value={d.type_de} onChange={(e) => setD({ ...d, type_de: e.target.value })} className={inputCls} data-testid="machine-type-de" /></div>
        <div><label className={labelCls}>Tipo (IT)</label>
          <input value={d.type_it} onChange={(e) => setD({ ...d, type_it: e.target.value })} className={inputCls} data-testid="machine-type-it" /></div>
        <div><label className={labelCls}>Beschreibung (DE)</label>
          <textarea rows={3} value={d.description_de} onChange={(e) => setD({ ...d, description_de: e.target.value })} className={inputCls} data-testid="machine-desc-de" /></div>
        <div><label className={labelCls}>Descrizione (IT)</label>
          <textarea rows={3} value={d.description_it} onChange={(e) => setD({ ...d, description_it: e.target.value })} className={inputCls} data-testid="machine-desc-it" /></div>
        <div className="sm:col-span-2"><label className={labelCls}>Technische Daten – eine pro Zeile: Label DE | Label IT | Wert</label>
          <textarea rows={4} value={specsToText(d.specs)} onChange={(e) => setD({ ...d, specs: textToSpecs(e.target.value) })} className={`${inputCls} font-mono-tech text-xs`} data-testid="machine-specs" /></div>
        <div><label className={labelCls}>Bild hochladen</label>
          <input type="file" accept="image/*" onChange={(e) => setImage(e.target.files[0])} className="text-xs text-slate-400" data-testid="machine-image" /></div>
        <div><label className={labelCls}>oder Bild-URL</label>
          <input value={d.image_url} onChange={(e) => setD({ ...d, image_url: e.target.value })} className={inputCls} data-testid="machine-image-url" /></div>
        <div><label className={labelCls}>Reihenfolge</label>
          <input type="number" value={d.order} onChange={(e) => setD({ ...d, order: Number(e.target.value) })} className={inputCls} data-testid="machine-order" /></div>
        <div><label className={labelCls}>Bereich</label>
          <select value={d.section || "fertigung"} onChange={(e) => setD({ ...d, section: e.target.value })} className={inputCls} data-testid="machine-section">
            <option value="fertigung">CNC-Maschinenpark</option>
            <option value="druck">Prototypenbau mittels 3D-Druck</option>
          </select></div>
        <label className="flex items-center gap-3 self-end pb-2 text-sm text-slate-300">
          <input type="checkbox" checked={!!d.coming_soon} onChange={(e) => setD({ ...d, coming_soon: e.target.checked })} className="h-4 w-4 accent-royal" data-testid="machine-coming-soon" />
          „Coming soon“-Banner anzeigen
        </label>
      </div>
      <div className="flex gap-3">
        <button type="submit" disabled={saving} className="bg-royal px-5 py-2.5 text-sm font-semibold text-white hover:bg-royal-hover disabled:opacity-50" data-testid="machine-save">{saving ? "…" : "Speichern"}</button>
        <button type="button" onClick={onDone} className="border border-white/20 px-5 py-2.5 text-sm text-slate-300 hover:border-white/50" data-testid="machine-cancel">Abbrechen</button>
      </div>
    </form>
  );
}

function GalleryForm({ initial, onDone }) {
  const [d, setD] = useState(initial || { title_de: "", title_it: "", category: "drehen", material: "", note_de: "", note_it: "", is_placeholder: false, image_url: "", order: 0 });
  const [image, setImage] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(d).forEach(([k, v]) => fd.append(k, v));
      if (image) fd.append("image", image);
      if (d.id) await axios.put(`${API}/admin/gallery/${d.id}`, fd, { headers: authH() });
      else await axios.post(`${API}/admin/gallery`, fd, { headers: authH() });
      toast.success("Gespeichert");
      onDone();
    } catch {
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-4 border border-white/10 bg-anthracite p-6" data-testid="gallery-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <div><label className={labelCls}>Titel (DE) *</label>
          <input required value={d.title_de} onChange={(e) => setD({ ...d, title_de: e.target.value })} className={inputCls} data-testid="gallery-title-de" /></div>
        <div><label className={labelCls}>Titolo (IT)</label>
          <input value={d.title_it} onChange={(e) => setD({ ...d, title_it: e.target.value })} className={inputCls} data-testid="gallery-title-it" /></div>
        <div><label className={labelCls}>Kategorie</label>
          <select value={d.category} onChange={(e) => setD({ ...d, category: e.target.value })} className={inputCls} data-testid="gallery-category">
            <option value="drehen">CNC-Drehen</option>
            <option value="fraesen">CNC-Fräsen</option>
            <option value="druck">3D-Druck</option>
          </select></div>
        <div><label className={labelCls}>Werkstoff</label>
          <input value={d.material} onChange={(e) => setD({ ...d, material: e.target.value })} className={inputCls} data-testid="gallery-material" /></div>
        <div><label className={labelCls}>Notiz (DE)</label>
          <input value={d.note_de} onChange={(e) => setD({ ...d, note_de: e.target.value })} className={inputCls} data-testid="gallery-note-de" /></div>
        <div><label className={labelCls}>Nota (IT)</label>
          <input value={d.note_it} onChange={(e) => setD({ ...d, note_it: e.target.value })} className={inputCls} data-testid="gallery-note-it" /></div>
        <div><label className={labelCls}>Bild hochladen</label>
          <input type="file" accept="image/*" onChange={(e) => setImage(e.target.files[0])} className="text-xs text-slate-400" data-testid="gallery-image" /></div>
        <div><label className={labelCls}>oder Bild-URL</label>
          <input value={d.image_url} onChange={(e) => setD({ ...d, image_url: e.target.value })} className={inputCls} data-testid="gallery-image-url" /></div>
        <div><label className={labelCls}>Reihenfolge</label>
          <input type="number" value={d.order} onChange={(e) => setD({ ...d, order: Number(e.target.value) })} className={inputCls} data-testid="gallery-order" /></div>
        <label className="flex items-center gap-3 self-end pb-2 text-sm text-slate-300">
          <input type="checkbox" checked={d.is_placeholder} onChange={(e) => setD({ ...d, is_placeholder: e.target.checked })} className="h-4 w-4 accent-royal" data-testid="gallery-placeholder" />
          Platzhalterbild
        </label>
      </div>
      <div className="flex gap-3">
        <button type="submit" disabled={saving} className="bg-royal px-5 py-2.5 text-sm font-semibold text-white hover:bg-royal-hover disabled:opacity-50" data-testid="gallery-save">{saving ? "…" : "Speichern"}</button>
        <button type="button" onClick={onDone} className="border border-white/20 px-5 py-2.5 text-sm text-slate-300 hover:border-white/50" data-testid="gallery-cancel">Abbrechen</button>
      </div>
    </form>
  );
}

const STATUSES = ["neu", "in Bearbeitung", "angeboten", "erledigt"];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("inquiries");
  const [inquiries, setInquiries] = useState([]);
  const [machines, setMachines] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null); // {type, item|undefined}

  const load = () => {
    axios.get(`${API}/admin/inquiries`, { headers: authH() }).then((r) => setInquiries(r.data)).catch((e) => { if (e.response?.status === 401) navigate("/admin"); });
    axios.get(`${API}/machines`).then((r) => setMachines(r.data)).catch(() => {});
    axios.get(`${API}/gallery`).then((r) => setGallery(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (!token()) { navigate("/admin"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setStatus = async (id, status) => {
    await axios.put(`${API}/admin/inquiries/${id}/status`, { status }, { headers: authH() });
    setInquiries(inquiries.map((i) => (i.id === id ? { ...i, status } : i)));
    if (selected?.id === id) setSelected({ ...selected, status });
  };

  const del = async (type, id) => {
    if (!window.confirm("Wirklich löschen?")) return;
    await axios.delete(`${API}/admin/${type}/${id}`, { headers: authH() });
    toast.success("Gelöscht");
    load();
  };

  const logout = () => { localStorage.removeItem("rm_admin_token"); navigate("/admin"); };

  const tabBtn = (key, label) => (
    <button key={key} onClick={() => { setTab(key); setEditing(null); }}
      className={`px-4 py-2.5 font-mono-tech text-xs uppercase tracking-widest transition-colors ${tab === key ? "bg-royal text-white" : "text-slate-400 hover:text-white"}`}
      data-testid={`admin-tab-${key}`}>{label}</button>
  );

  return (
    <div className="min-h-screen bg-anthracite-dark text-slate-100" data-testid="admin-dashboard">
      <header className="flex items-center justify-between border-b border-white/10 bg-anthracite px-4 py-4 sm:px-8">
        <span className="flex items-center gap-2 font-black tracking-tight text-white"><img src="/logo-mark.webp" alt="" className="h-6 w-auto" /> R-MECHANIK <span className="font-mono-tech text-xs font-normal text-slate-500">ADMIN</span></span>
        <button onClick={logout} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white" data-testid="admin-logout"><LogOut size={15} /> Logout</button>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-8">
        <div className="flex gap-1 border-b border-white/10">
          {tabBtn("inquiries", `Anfragen (${inquiries.length})`)}
          {tabBtn("machines", "Maschinenpark")}
          {tabBtn("gallery", "Galerie")}
        </div>

        {tab === "inquiries" && (
          <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_380px]">
            <div className="space-y-2" data-testid="inquiries-list">
              {inquiries.length === 0 && <p className="py-12 text-center text-sm text-slate-500">Noch keine Anfragen.</p>}
              {inquiries.map((inq, i) => (
                <button key={inq.id} onClick={() => setSelected(inq)}
                  className={`flex w-full flex-wrap items-center justify-between gap-3 border p-4 text-left transition-colors ${selected?.id === inq.id ? "border-royal bg-royal/10" : "border-white/10 bg-anthracite hover:border-white/30"}`}
                  data-testid={`inquiry-row-${i}`}>
                  <div>
                    <p className="text-sm font-semibold text-white">{inq.name} {inq.company && <span className="text-slate-400">· {inq.company}</span>}</p>
                    <p className="mt-1 text-xs text-slate-500">{new Date(inq.created_at).toLocaleString("de-DE")} · {inq.service || "—"} · {inq.files?.length || 0} Datei(en)</p>
                  </div>
                  <span className={`px-2.5 py-1 font-mono-tech text-[10px] uppercase tracking-widest ${inq.status === "neu" ? "bg-royal/20 text-royal-soft" : "bg-white/10 text-slate-300"}`}>{inq.status}</span>
                </button>
              ))}
            </div>
            {selected && (
              <div className="h-fit border border-white/10 bg-anthracite p-6 lg:sticky lg:top-8" data-testid="inquiry-detail">
                <h3 className="font-bold text-white">{selected.name}</h3>
                <dl className="mt-4 space-y-2.5 text-sm">
                  {[["Firma", selected.company], ["E-Mail", selected.email], ["Telefon", selected.phone], ["Adresse", selected.address],
                    ["Leistung", selected.service], ["Werkstoff", selected.material], ["Stückzahl", selected.quantity], ["Liefertermin", selected.delivery_date]]
                    .map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-4 border-b border-white/5 pb-2">
                        <dt className="text-slate-500">{k}</dt><dd className="text-right text-slate-200">{v || "—"}</dd>
                      </div>
                    ))}
                </dl>
                <p className="mt-4 text-xs uppercase tracking-widest text-slate-500">Nachricht</p>
                <p className="mt-1 whitespace-pre-line text-sm text-slate-300">{selected.message}</p>
                {selected.files?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs uppercase tracking-widest text-slate-500">Dateien</p>
                    <ul className="mt-2 space-y-1.5">
                      {selected.files.map((file, i) => (
                        <li key={i}>
                          <a href={`${BACKEND}/api/files/${file.path}?auth=${token()}`} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-2 text-sm text-royal-soft hover:text-white" data-testid={`inquiry-file-${i}`}>
                            <Download size={13} /> {file.filename} ({Math.round(file.size / 1024)} KB)
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="mt-5">
                  <label className={labelCls}>Status</label>
                  <select value={selected.status} onChange={(e) => setStatus(selected.id, e.target.value)} className={inputCls} data-testid="inquiry-status-select">
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "machines" && (
          <div className="mt-8 space-y-4" data-testid="machines-admin">
            {!editing && (
              <button onClick={() => setEditing({ type: "machines" })} className="flex items-center gap-2 bg-royal px-4 py-2.5 text-sm font-semibold text-white hover:bg-royal-hover" data-testid="machine-add">
                <Plus size={15} /> Maschine hinzufügen
              </button>
            )}
            {editing?.type === "machines" && <MachineForm initial={editing.item} onDone={() => { setEditing(null); load(); }} />}
            {machines.map((m, i) => (
              <div key={m.id} className="flex items-center justify-between border border-white/10 bg-anthracite p-4" data-testid={`machine-row-${i}`}>
                <div>
                  <p className="font-semibold text-white">{m.name}</p>
                  <p className="text-xs text-slate-500">{m.type_de}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setEditing({ type: "machines", item: m })} className="p-2 text-slate-400 hover:text-white" data-testid={`machine-edit-${i}`}><Pencil size={16} /></button>
                  <button onClick={() => del("machines", m.id)} className="p-2 text-slate-400 hover:text-red-400" data-testid={`machine-delete-${i}`}><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "gallery" && (
          <div className="mt-8 space-y-4" data-testid="gallery-admin">
            {!editing && (
              <button onClick={() => setEditing({ type: "gallery" })} className="flex items-center gap-2 bg-royal px-4 py-2.5 text-sm font-semibold text-white hover:bg-royal-hover" data-testid="gallery-add">
                <Plus size={15} /> Werkstück hinzufügen
              </button>
            )}
            {editing?.type === "gallery" && <GalleryForm initial={editing.item} onDone={() => { setEditing(null); load(); }} />}
            {gallery.map((g, i) => (
              <div key={g.id} className="flex items-center justify-between border border-white/10 bg-anthracite p-4" data-testid={`gallery-row-${i}`}>
                <div className="flex items-center gap-4">
                  {g.image_url && <img src={g.image_url.startsWith("http") ? g.image_url : `${BACKEND}${g.image_url}`} alt={g.title_de} className="h-12 w-16 object-cover" />}
                  <div>
                    <p className="font-semibold text-white">{g.title_de} {g.is_placeholder && <span className="ml-2 font-mono-tech text-[10px] uppercase text-amber-300">Platzhalter</span>}</p>
                    <p className="text-xs text-slate-500">{g.category} · {g.material}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setEditing({ type: "gallery", item: g })} className="p-2 text-slate-400 hover:text-white" data-testid={`gallery-edit-${i}`}><Pencil size={16} /></button>
                  <button onClick={() => del("gallery", g.id)} className="p-2 text-slate-400 hover:text-red-400" data-testid={`gallery-delete-${i}`}><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
